import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { getSupabase } from '../lib/supabaseClient';
import { useBackendMode } from '../lib/useBackendMode';
import { useTableRealtime } from '../lib/useTableRealtime';
import { IOS_CATEGORY_LIST, IOS_COST_CATEGORIES, colorForCategoryName } from '../lib/iosCostCategories';
import { logUserActivity } from '../lib/logUserActivity';

/**
 * Cost Tracker, wired to `public.cost_tracker_expenses` + `public.profiles.budget`
 * + `public.budget_categories` master list (100 mobile-app categories).
 *
 * iOS-parity schema notes (Feb 26, 2026 update):
 *   - `cost_tracker_expenses.cost` stores the line-item NAME (string,
 *     e.g. "Soil test", "Kitchen cabinetry"), matches iOS
 *     `addCostTrackerExpense` exactly. The line-item is what shows as
 *     the expense title both on web and iOS.
 *   - `category_id` is also written (FK to `budget_categories`) so the
 *     pie/bar chart aggregation by category still works. The web stores
 *     a NEGATIVE numeric id (e.g. -1, -2) for the iOS-hardcoded
 *     categories so they don't collide with Supabase auto-generated
 *     positive ids.
 *   - `amount` is also mirrored to legacy `cost` column? NO, iOS uses
 *     `cost` for the category-name string. The previous behaviour
 *     (mirroring amount to `cost`) was a web-only mistake; corrected
 *     Feb 26, 2026.
 *   - `build_type` filters expenses by project (multi-project premium
 *     users). NULL build_type rows = legacy/single-project users; we
 *     include them in every project view to avoid losing data.
 *   - `notes` column is plain notes (was previously packing title +
 *     notes into a single field; that's gone now that we use the
 *     line-item NAME as the title).
 *   - Date filtering uses `created_at` (set by DB on insert).
 *   - Profile budget lives on `profiles.budget`.
 */

const DEFAULT_BUDGET = 480000;

// `profiles.budget` is stored as TEXT in Supabase (legacy schema, iOS
// app sometimes writes strings like "$450,000" via ProjectSetupPage).
// Parse defensively: accept numbers AND any currency-formatted string,
// stripping commas, spaces and the leading "$". Returns null if the
// value can't be parsed to a positive number, caller falls back to
// DEFAULT_BUDGET in that case.
const parseBudget = (raw) => {
  if (raw === null || raw === undefined || raw === '') return null;
  if (typeof raw === 'number') return raw > 0 ? raw : null;
  const n = parseFloat(String(raw).replace(/[^0-9.]/g, ''));
  return Number.isFinite(n) && n > 0 ? n : null;
};

// 12-colour palette, hash a category_id onto it deterministically so the
// same category always gets the same colour. Lifted from the brand spec.
const PALETTE = [
  '#1d8efe', '#10b981', '#f59e0b', '#ec4899', '#8b5cf6', '#64748b',
  '#06b6d4', '#a16207', '#ef4444', '#22c55e', '#6366f1', '#0891b2',
];
const colorForId = (id) => PALETTE[Math.abs(Number(id) || 0) % PALETTE.length];

// Reverse lookup: line-item string ("Soil test") → category name ("Site & Surveys").
// Built once at module load from `IOS_COST_CATEGORIES`. Used to infer the
// category for iOS-style rows where the DB has NO `category_id` (iOS never
// writes one, its category info lives entirely in the local lookup, see
// `database.ts:147 addCostTrackerExpense` which doesn't include category_id).
const ITEM_TO_CATEGORY = (() => {
  const map = {};
  Object.entries(IOS_COST_CATEGORIES).forEach(([catName, items]) => {
    items.forEach((it) => { map[it.toLowerCase()] = catName; });
  });
  return map;
})();
const categoryNameForLineItem = (lineItem) => {
  if (!lineItem) return null;
  return ITEM_TO_CATEGORY[String(lineItem).toLowerCase()] || null;
};

const storageKey = (userId, suffix) =>
  `buildpilot.expenses.${userId || 'anon'}.${suffix}`;

const readJSON = (key, fallback) => {
  try { const raw = localStorage.getItem(key); return raw ? JSON.parse(raw) : fallback; }
  catch { return fallback; }
};
const writeJSON = (key, value) => {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* no-op */ }
};

const uid = () => `exp_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

// Pack title + notes as separate columns now (iOS parity). Title = the
// line-item name lives in `cost` column. `notes` is plain user notes.
// Legacy rows that packed title+notes via "\n" still parse correctly via
// `unpackNotes` for read-back compatibility.
const unpackNotes = (raw) => {
  if (!raw) return { title: '', notes: '' };
  const idx = raw.indexOf('\n');
  if (idx === -1) return { title: '', notes: raw };
  return { title: raw.slice(0, idx), notes: raw.slice(idx + 1) };
};

const fromRow = (r, categoryMap) => {
  // Title priority: `cost` column (iOS-style line-item name) > legacy
  // packed-notes first line > fallback to the category name.
  const { title: legacyTitle, notes: legacyNotes } = unpackNotes(r.notes);
  // `r.cost` in iOS schema is the line-item NAME (string). In legacy
  // web rows it was the amount mirror (number). Detect string vs number
  // so we don't show "12000" as the title for old rows.
  const costIsName = typeof r.cost === 'string' && Number.isNaN(Number(r.cost));
  const title = costIsName ? r.cost : (legacyTitle || 'Untitled expense');
  // Category resolution priority:
  // 1. Explicit category_id FK on the row → look up in categoryMap
  // 2. If cost is a known iOS line-item, reverse-lookup the category
  //    from IOS_COST_CATEGORIES (handles iOS-written rows that have no
  //    category_id)
  // 3. Fall back to "Uncategorised"
  const explicitCategory = r.category_id ? categoryMap[r.category_id] : null;
  const inferredCategoryName = !explicitCategory && costIsName
    ? categoryNameForLineItem(r.cost)
    : null;
  const categoryName = explicitCategory?.name
    || inferredCategoryName
    || categoryMap[r.category_id]?.name
    || 'Uncategorised';
  // Color: prefer the iOS category palette key, fallback to id-based.
  const inferredCategory = inferredCategoryName
    ? categoryMap[`name::${inferredCategoryName.toLowerCase()}`]
    : null;
  const categoryColor = (explicitCategory?.color)
    || (inferredCategory?.color)
    || colorForId(r.category_id || (categoryName ? categoryName.length : 0));
  return {
    id: r.id,
    title,
    amount: Number(r.amount) || (costIsName ? 0 : Number(r.cost) || 0),
    categoryId: r.category_id || inferredCategory?.id || null,
    categoryName,
    categoryColor,
    date: r.created_at ? r.created_at.slice(0, 10) : new Date().toISOString().slice(0, 10),
    notes: r.notes && !legacyTitle ? r.notes : legacyNotes,
    buildType: r.build_type || null,
    createdAt: r.created_at,
  };
};

/**
 * @param {Object} [opts]
 * @param {string|null} [opts.buildType] - When provided, only expenses
 *   matching this `build_type` (plus legacy rows where build_type is
 *   NULL) are returned. Used by the multi-project Cost Tracker so each
 *   project shows its own spend.
 */
export const useExpensesStore = (opts = {}) => {
  const { buildType = null } = opts;
  const { user } = useAuth();
  const userId = user?.id;
  const mode = useBackendMode('cost_tracker_expenses');

  // Seed the master list with the iOS hard-coded categories so the picker
  // always offers the same options as the mobile app. Supabase rows (if
  // any) merge on top by id.
  const initialIosMap = useMemo(() => {
    const m = {};
    IOS_CATEGORY_LIST.forEach((c) => { m[c.id] = c; m[`name::${c.name.toLowerCase()}`] = c; });
    return m;
  }, []);
  const [categoriesById, setCategoriesById] = useState(initialIosMap);
  const [expenses, setExpenses] = useState([]);
  const [budget, setBudgetState] = useState(DEFAULT_BUDGET);
  const [hydrated, setHydrated] = useState(false);

  // Load the master list once per session (public master data, no RLS).
  // Supabase rows are MERGED with the iOS hard-coded list, so the iOS
  // categories are always available even if the DB has been wiped.
  useEffect(() => {
    let cancelled = false;
    const loadCategories = async () => {
      if (mode !== 'remote') return;
      try {
        const supabase = getSupabase();
        const { data, error } = await supabase.from('budget_categories').select('id, name').order('name');
        if (cancelled) return;
        if (error) throw error;
        if (!data || data.length === 0) return;
        setCategoriesById((cur) => {
          const next = { ...cur };
          (data || []).forEach((c) => {
            const entry = { id: c.id, name: c.name, color: colorForCategoryName(c.name) };
            next[c.id] = entry;
            next[`name::${c.name.toLowerCase()}`] = entry;
          });
          return next;
        });
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('[expenses] budget_categories load failed:', e?.message);
      }
    };
    loadCategories();
    return () => { cancelled = true; };
  }, [mode]);

  // Hydrate rows + budget
  useEffect(() => {
    let cancelled = false;
    const hydrate = async () => {
      if (mode === 'booting') return;
      if (mode === 'remote' && userId) {
        try {
          const supabase = getSupabase();
          const [rowsRes, profileRes] = await Promise.all([
            supabase.from('cost_tracker_expenses').select('*').eq('user_id', userId).order('created_at', { ascending: false }),
            supabase.from('profiles').select('budget').eq('id', userId).maybeSingle(),
          ]);
          if (cancelled) return;
          if (rowsRes.error) throw rowsRes.error;
          setExpenses((rowsRes.data || []).map((r) => fromRow(r, categoriesById)));
          const remoteBudget = parseBudget(profileRes?.data?.budget);
          setBudgetState(remoteBudget !== null ? remoteBudget : DEFAULT_BUDGET);
          setHydrated(true);
          return;
        } catch (e) {
          // eslint-disable-next-line no-console
          console.warn('[expenses] remote hydrate failed:', e?.message);
        }
      }
      const e = readJSON(storageKey(userId, 'expenses'), []);
      const b = readJSON(storageKey(userId, 'budget'), DEFAULT_BUDGET);
      if (cancelled) return;
      setExpenses(Array.isArray(e) ? e : []);
      setBudgetState(typeof b === 'number' ? b : DEFAULT_BUDGET);
      setHydrated(true);
    };
    hydrate();
    return () => { cancelled = true; };
    // Re-hydrate when categoriesById fills in so row.categoryName is correct
  }, [mode, userId, categoriesById]);

  useEffect(() => {
    if (!hydrated || mode !== 'local') return;
    writeJSON(storageKey(userId, 'expenses'), expenses);
  }, [expenses, hydrated, userId, mode]);

  useEffect(() => {
    if (!hydrated || mode !== 'local') return;
    writeJSON(storageKey(userId, 'budget'), budget);
  }, [budget, hydrated, userId, mode]);

  // Realtime, pick up rows the mobile app inserts / updates / deletes.
  // Dedupes against optimistic local inserts by row id.
  useTableRealtime({
    table: 'cost_tracker_expenses',
    userId,
    mode,
    onInsert: (row) => {
      setExpenses((cur) => {
        if (cur.find((e) => e.id === row.id)) return cur;
        return [fromRow(row, categoriesById), ...cur];
      });
    },
    onUpdate: (row) => {
      setExpenses((cur) => cur.map((e) => (e.id === row.id ? fromRow(row, categoriesById) : e)));
    },
    onDelete: (row) => {
      setExpenses((cur) => cur.filter((e) => e.id !== row.id));
    },
  });

  // Realtime, budget changes on the profiles row (rare, but the mobile
  // app may set it on signup). Filter on `id=eq.userId` because profiles
  // uses `id` (not `user_id`). Unique channel suffix per hook instance.
  const profileChannelId = useRef(Math.random().toString(36).slice(2, 8));
  useEffect(() => {
    if (mode !== 'remote' || !userId) return undefined;
    const supabase = getSupabase();
    const ch = supabase
      .channel(`bp-profiles-${userId}-${profileChannelId.current}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'profiles',
        filter: `id=eq.${userId}`,
      }, (payload) => {
        const remoteBudget = parseBudget(payload.new?.budget);
        if (remoteBudget !== null) {
          setBudgetState(remoteBudget);
        }
      })
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [mode, userId]);

  const addExpense = useCallback(async (data) => {
    const amount = Number(data.amount) || 0;
    const categoryId = Number(data.categoryId) || null;
    // The `title` IS the line-item name (e.g. "Soil test"). iOS stores
    // this in the `cost` column. If no title provided, fall back to the
    // category name so the row still renders something readable.
    const lineItemName = (data.title || categoriesById[categoryId]?.name || 'Untitled expense').trim();
    const optimistic = {
      id: uid(),
      title: lineItemName,
      amount,
      categoryId,
      categoryName: categoriesById[categoryId]?.name || 'Uncategorised',
      categoryColor: colorForId(categoryId),
      date: data.date || new Date().toISOString().slice(0, 10),
      notes: data.notes || '',
      buildType: data.buildType || buildType || null,
      createdAt: new Date().toISOString(),
    };
    setExpenses((cur) => [optimistic, ...cur]);
    if (mode === 'remote' && userId) {
      try {
        const supabase = getSupabase();
        const insertPayload = {
          user_id: userId,
          amount,
          // iOS-parity: `cost` column stores the line-item NAME string,
          // NOT the amount. Crucial for cross-app compatibility.
          cost: lineItemName,
          notes: data.notes ? data.notes.trim() : null,
        };
        // Only write `category_id` if it's a REAL `budget_categories` FK
        // (positive integer). iOS uses hardcoded negative ids in its
        // local category list which would violate the FK constraint , 
        // iOS itself doesn't write category_id at all, so we follow the
        // same pattern. Category is inferred on read from the `cost`
        // string via the IOS_COST_CATEGORIES lookup.
        if (categoryId && categoryId > 0) insertPayload.category_id = categoryId;
        // Only set build_type when provided, single-project users
        // leave it NULL so their rows show in every project view.
        if (optimistic.buildType) insertPayload.build_type = optimistic.buildType;
        const { data: inserted, error } = await supabase.from('cost_tracker_expenses').insert(insertPayload).select('*').single();
        if (error) throw error;
        setExpenses((cur) => cur.map((r) => (r.id === optimistic.id ? fromRow(inserted, categoriesById) : r)));
        logUserActivity({
          type: 'expense',
          title: 'Cost added',
          description: `${lineItemName}, $${amount.toLocaleString()}`,
        });
        return fromRow(inserted, categoriesById);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('[expenses] insert failed:', e?.message);
        setExpenses((cur) => cur.filter((r) => r.id !== optimistic.id));
        return null;
      }
    }
    return optimistic;
  }, [mode, userId, categoriesById, buildType]);

  const updateExpense = useCallback(async (id, patch) => {
    setExpenses((cur) =>
      cur.map((e) =>
        e.id === id
          ? {
            ...e,
            ...patch,
            amount: Number(patch.amount ?? e.amount) || 0,
            categoryName: patch.categoryId
              ? (categoriesById[patch.categoryId]?.name || e.categoryName)
              : e.categoryName,
            categoryColor: patch.categoryId ? colorForId(patch.categoryId) : e.categoryColor,
          }
          : e
      )
    );
    if (mode === 'remote' && userId) {
      try {
        const supabase = getSupabase();
        const dbPatch = { updated_at: new Date().toISOString() };
        // iOS schema: line-item name → `cost` column, free-text → `notes`.
        if (patch.title !== undefined) dbPatch.cost = String(patch.title).trim();
        if (patch.notes !== undefined) dbPatch.notes = patch.notes ? String(patch.notes).trim() : null;
        if (patch.amount !== undefined) {
          dbPatch.amount = Number(patch.amount) || 0;
        }
        if (patch.categoryId !== undefined) dbPatch.category_id = Number(patch.categoryId) || null;
        const { error } = await supabase.from('cost_tracker_expenses').update(dbPatch).eq('id', id).eq('user_id', userId);
        if (error) throw error;
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('[expenses] update failed:', e?.message);
      }
    }
  }, [mode, userId, categoriesById]);

  const removeExpense = useCallback(async (id) => {
    setExpenses((cur) => cur.filter((e) => e.id !== id));
    if (mode === 'remote' && userId) {
      try {
        const supabase = getSupabase();
        const { error } = await supabase.from('cost_tracker_expenses').delete().eq('id', id).eq('user_id', userId);
        if (error) throw error;
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('[expenses] delete failed:', e?.message);
      }
    }
  }, [mode, userId]);

  const setBudget = useCallback(async (n) => {
    const value = Number(n) || 0;
    setBudgetState(value);
    if (mode === 'remote' && userId) {
      try {
        const supabase = getSupabase();
        const { error } = await supabase.from('profiles').upsert(
          { id: userId, budget: value, updated_at: new Date().toISOString() },
          { onConflict: 'id' }
        );
        if (error) throw error;
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('[expenses] budget upsert failed:', e?.message);
      }
    }
  }, [mode, userId]);

  // Build-type filtered view (multi-project parity, Feb 26, 2026).
  // Rows with NULL build_type are legacy/single-project, include them
  // in every project view so users don't "lose" data after switching to
  // multi-project mode.
  const filteredExpenses = useMemo(() => {
    if (!buildType) return expenses;
    return expenses.filter((e) => !e.buildType || e.buildType === buildType);
  }, [expenses, buildType]);

  // Derived (all calculated against the active project's filtered list)
  const totalSpent = filteredExpenses.reduce((sum, e) => sum + (Number(e.amount) || 0), 0);
  const remaining = Math.max(0, budget - totalSpent);
  const overBudget = Math.max(0, totalSpent - budget);
  const percentUsed = budget > 0 ? Math.min(100, (totalSpent / budget) * 100) : 0;

  // Aggregate by category for the breakdown card. Only categories the user
  // actually has expenses in show up.
  const byCategory = useMemo(() => {
    const groups = {};
    filteredExpenses.forEach((e) => {
      const key = e.categoryId || 0;
      if (!groups[key]) {
        groups[key] = {
          id: e.categoryId,
          name: e.categoryName,
          color: e.categoryColor,
          total: 0,
        };
      }
      groups[key].total += Number(e.amount) || 0;
    });
    const list = Object.values(groups).sort((a, b) => b.total - a.total);
    return list.map((g) => ({
      ...g,
      key: String(g.id || 0),
      label: g.name,
      percent: totalSpent > 0 ? (g.total / totalSpent) * 100 : 0,
    }));
  }, [filteredExpenses, totalSpent]);

  // The picker shows ONLY the iOS-spec category list (mirrors mobile app
  // exactly). DB-loaded categories from older expenses are kept inside
  // `categoriesById` purely for lookup (so legacy expenses still render
  // with their old category name) but are excluded from this list.
  const allCategories = useMemo(() => {
    const seenNames = new Set();
    const list = [];
    // iOS list only, in spec order, negative-id entries seeded above.
    Object.values(categoriesById).forEach((c) => {
      if (!c || !c.name || c.id >= 0) return;
      const key = c.name.toLowerCase();
      if (seenNames.has(key)) return;
      seenNames.add(key);
      list.push(c);
    });
    return list;
  }, [categoriesById]);

  return {
    hydrated,
    mode,
    // `expenses` returns the build-type-filtered view (or all expenses
    // if no build_type filter is set). `allExpenses` is the unfiltered
    // raw list, used for the cross-project analytics widget.
    expenses: filteredExpenses,
    allExpenses: expenses,
    activeBuildType: buildType,
    budget,
    totalSpent,
    remaining,
    overBudget,
    percentUsed,
    byCategory,
    allCategories,
    categoriesById,
    addExpense,
    updateExpense,
    removeExpense,
    setBudget,
  };
};

export const formatCurrency = (n, opts = {}) =>
  new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    maximumFractionDigits: opts.cents ? 2 : 0,
  }).format(Number(n) || 0);

export const formatDate = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return iso;
  }
};
